/**
 * Carmin Motion — Scroll Animation Engine
 * Replaces scattered IntersectionObserver implementations with a single,
 * performant, data-attribute-driven animation system.
 *
 * Usage:
 *   <div data-motion="fade-up">...</div>
 *   <div data-motion="fade-in" data-motion-delay="0.3">...</div>
 *   <div data-motion-stagger="0.08">
 *     <div data-motion="fade-up">A</div>
 *     <div data-motion="fade-up">B</div>
 *   </div>
 *   <span data-motion-counter="150">0</span>
 *   <div data-motion-parallax="0.1">...</div>
 */
(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────────────────
  var SPRING_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
  var DEFAULT_DURATION = 0.7;       // seconds
  var DEFAULT_THRESHOLD = 0.15;
  var COUNTER_DURATION = 2000;      // ms

  // Animation presets: [property, from, to]
  var PRESETS = {
    'fade-up':    { opacity: [0, 1], transform: ['translateY(30px)',  'translateY(0)'] },
    'fade-in':    { opacity: [0, 1] },
    'fade-left':  { opacity: [0, 1], transform: ['translateX(-30px)', 'translateX(0)'] },
    'fade-right': { opacity: [0, 1], transform: ['translateX(30px)',  'translateX(0)'] },
    'scale-in':   { opacity: [0, 1], transform: ['scale(0.95)',       'scale(1)'] },
    'slide-up':   { transform: ['translateY(40px)', 'translateY(0)'] }
  };

  // ── Reduced-motion detection ───────────────────────────────────────────
  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReduced) {
    document.documentElement.classList.add('motion-reduced');
  }

  // ── Utility helpers ────────────────────────────────────────────────────

  /** Parse a float from a data attribute, return fallback if missing/NaN */
  function getAttrFloat(el, attr, fallback) {
    var val = el.getAttribute(attr);
    if (val === null) return fallback;
    var num = parseFloat(val);
    return isNaN(num) ? fallback : num;
  }

  /** easeOutExpo for counter animation */
  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  // ── Core engine ────────────────────────────────────────────────────────

  var observer = null;
  var parallaxElements = [];
  var rafParallax = null;
  var lastScrollY = 0;
  var ticking = false;

  /**
   * Apply the "before" state so elements are hidden until animated.
   */
  function prepareElement(el) {
    var preset = PRESETS[el.getAttribute('data-motion')];
    if (!preset) return;

    // Reduced motion: only handle opacity, skip transforms
    if (prefersReduced) {
      if (preset.opacity) {
        el.style.opacity = '0';
      }
      return;
    }

    el.style.willChange = 'transform, opacity';

    if (preset.opacity) {
      el.style.opacity = String(preset.opacity[0]);
    }
    if (preset.transform) {
      el.style.transform = preset.transform[0];
    }
  }

  /**
   * Animate an element into view.
   */
  function animateElement(el) {
    var motionType = el.getAttribute('data-motion');
    var preset = PRESETS[motionType];
    if (!preset) return;

    var duration = getAttrFloat(el, 'data-motion-duration', DEFAULT_DURATION);
    var delay = getAttrFloat(el, 'data-motion-delay', 0);

    // Reduced motion: instant opacity fade only
    if (prefersReduced) {
      if (preset.opacity) {
        el.style.transition = 'opacity 0.3s ease ' + delay + 's';
        el.style.opacity = '1';
      }
      el.setAttribute('data-motion-done', 'true');
      return;
    }

    // Build keyframes for Web Animations API
    var supportsWAAPI = typeof el.animate === 'function';

    if (supportsWAAPI) {
      var from = {};
      var to = {};

      if (preset.opacity) {
        from.opacity = preset.opacity[0];
        to.opacity = preset.opacity[1];
      }
      if (preset.transform) {
        from.transform = preset.transform[0];
        to.transform = preset.transform[1];
      }

      var anim = el.animate([from, to], {
        duration: duration * 1000,
        delay: delay * 1000,
        easing: SPRING_EASE,
        fill: 'forwards'
      });

      anim.onfinish = function () {
        // Commit final state to inline styles and clean up
        if (preset.opacity)   el.style.opacity = String(preset.opacity[1]);
        if (preset.transform) el.style.transform = preset.transform[1];
        el.style.willChange = 'auto';
        anim.cancel(); // free memory
      };

    } else {
      // CSS transition fallback
      var props = [];
      if (preset.opacity)   props.push('opacity');
      if (preset.transform) props.push('transform');

      el.style.transition = props.map(function (p) {
        return p + ' ' + duration + 's ' + SPRING_EASE + ' ' + delay + 's';
      }).join(', ');

      // Force reflow then apply "to" values
      void el.offsetWidth;

      if (preset.opacity)   el.style.opacity = String(preset.opacity[1]);
      if (preset.transform) el.style.transform = preset.transform[1];

      // Clean up will-change after transition ends
      var cleanup = function () {
        el.style.willChange = 'auto';
        el.removeEventListener('transitionend', cleanup);
      };
      el.addEventListener('transitionend', cleanup);
    }

    el.setAttribute('data-motion-done', 'true');
  }

  /**
   * Animate a counter element from 0 to target value.
   */
  function animateCounter(el) {
    var target = parseFloat(el.getAttribute('data-motion-counter'));
    if (isNaN(target)) return;

    var isInt = target % 1 === 0;
    var start = performance.now();

    function step(now) {
      var elapsed = now - start;
      var progress = Math.min(elapsed / COUNTER_DURATION, 1);
      var value = easeOutExpo(progress) * target;

      el.textContent = isInt ? Math.round(value) : value.toFixed(1);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
    el.setAttribute('data-motion-counter-done', 'true');
  }

  // ── IntersectionObserver callback ──────────────────────────────────────

  function onIntersect(entries) {
    // Batch via rAF for performance
    requestAnimationFrame(function () {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;

        var el = entry.target;
        var once = el.getAttribute('data-motion-once') !== 'false';

        // Counter element
        if (el.hasAttribute('data-motion-counter') && !el.hasAttribute('data-motion-counter-done')) {
          animateCounter(el);
          if (once) observer.unobserve(el);
          continue;
        }

        // Motion element
        if (el.hasAttribute('data-motion') && !el.hasAttribute('data-motion-done')) {
          animateElement(el);
          if (once) observer.unobserve(el);
        }
      }
    });
  }

  // ── Stagger logic ─────────────────────────────────────────────────────

  /**
   * For parent elements with data-motion-stagger, calculate and apply
   * incremental delays to each child that has data-motion.
   */
  function applyStagger() {
    var parents = document.querySelectorAll('[data-motion-stagger]');
    for (var i = 0; i < parents.length; i++) {
      var parent = parents[i];
      var gap = getAttrFloat(parent, 'data-motion-stagger', 0.08);
      var children = parent.querySelectorAll('[data-motion]');

      for (var j = 0; j < children.length; j++) {
        // Only set delay if not already manually specified
        if (!children[j].hasAttribute('data-motion-delay')) {
          children[j].setAttribute('data-motion-delay', String(j * gap));
        }
      }
    }
  }

  // ── Parallax system ───────────────────────────────────────────────────

  function updateParallax() {
    lastScrollY = window.pageYOffset || document.documentElement.scrollTop;

    for (var i = 0; i < parallaxElements.length; i++) {
      var item = parallaxElements[i];
      var rect = item.el.getBoundingClientRect();
      var viewH = window.innerHeight;

      // Only process if roughly in viewport
      if (rect.bottom < -viewH || rect.top > viewH * 2) continue;

      var centerOffset = rect.top + rect.height / 2 - viewH / 2;
      var yShift = centerOffset * item.factor;
      item.el.style.transform = 'translateY(' + yShift.toFixed(1) + 'px)';
    }

    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateParallax);
    }
  }

  function initParallax() {
    if (prefersReduced) return;

    parallaxElements = [];
    var els = document.querySelectorAll('[data-motion-parallax]');

    for (var i = 0; i < els.length; i++) {
      var factor = getAttrFloat(els[i], 'data-motion-parallax', 0.1);
      els[i].style.willChange = 'transform';
      parallaxElements.push({ el: els[i], factor: factor });
    }

    if (parallaxElements.length > 0) {
      window.addEventListener('scroll', onScroll, { passive: true });
      updateParallax();
    }
  }

  // ── Scan and observe ──────────────────────────────────────────────────

  function scan() {
    // Apply stagger delays before observing
    applyStagger();

    // Gather all motion elements and counters
    var motionEls = document.querySelectorAll('[data-motion]:not([data-motion-done])');
    var counterEls = document.querySelectorAll('[data-motion-counter]:not([data-motion-counter-done])');

    // Group by threshold for a single observer per threshold
    var thresholdMap = {};

    function addToMap(el) {
      var t = getAttrFloat(el, 'data-motion-threshold', DEFAULT_THRESHOLD);
      var key = String(t);
      if (!thresholdMap[key]) thresholdMap[key] = [];
      thresholdMap[key].push(el);
    }

    for (var i = 0; i < motionEls.length; i++) {
      prepareElement(motionEls[i]);
      addToMap(motionEls[i]);
    }

    for (var j = 0; j < counterEls.length; j++) {
      addToMap(counterEls[j]);
    }

    // Create one observer per unique threshold value
    var keys = Object.keys(thresholdMap);
    for (var k = 0; k < keys.length; k++) {
      var threshold = parseFloat(keys[k]);
      var obs = new IntersectionObserver(onIntersect, {
        threshold: threshold,
        rootMargin: '0px 0px -40px 0px'
      });

      var group = thresholdMap[keys[k]];
      for (var m = 0; m < group.length; m++) {
        obs.observe(group[m]);
      }

      // Keep reference to the primary observer
      if (!observer) observer = obs;
    }

    // Init parallax
    initParallax();
  }

  // ── Public API ────────────────────────────────────────────────────────

  window.CarminMotion = {
    /** Re-scan the DOM for new elements (e.g. after AJAX or section load) */
    refresh: function () {
      scan();
    }
  };

  // ── Initialization ────────────────────────────────────────────────────

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Re-scan when Shopify reloads a section in the theme editor
  document.addEventListener('shopify:section:load', function () {
    scan();
  });

})();
